import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Step1PrintType from "./steps/Step1PrintType";
import Step2GuestCount from "./steps/Step2GuestCount";
import Step3Delivery from "./steps/Step3Delivery";
import Step4PostalCode from "./steps/Step4PostalCode";
import StepEventType from "./steps/StepEventType";
import StepFlatrate from "./steps/StepFlatrate";
import StepExtras from "./steps/StepExtras";
import StepDigital from "./steps/StepDigital";
import WizardResults from "./WizardResults";
import { getMaxGuestsForPaperStorage } from "@/utils/qualityConfig";
import { mapProductToCard } from "@/utils/mapProductToCard";
import { trackWizardEvent } from "@/utils/wizardTracking";

export default function GuruWizard() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    eventType: null,
    printType: null,
    wantsFlatrate: null,
    guestCount: null,
    delivery: null,
    postalCode: null,
    extras: [],
    digitalOptions: [],
  });
  const [flatratePrices, setFlatratePrices] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [resultsKey, setResultsKey] = useState(null);
  const [lastTrackedView, setLastTrackedView] = useState(null);

  const buildWizardDataFromQuery = (queryData = {}) => ({
    eventType: queryData.eventType || null,
    printType: queryData.printType || null,
    wantsFlatrate:
      queryData.wantsFlatrate === "true"
        ? true
        : queryData.wantsFlatrate === "false"
        ? false
        : null,
    guestCount: queryData.guestCount || null,
    delivery: queryData.delivery || null,
    postalCode: queryData.postalCode || null,
    extras: queryData.extras ? queryData.extras.split(",") : [],
    digitalOptions: queryData.digitalOptions
      ? queryData.digitalOptions.split(",")
      : [],
  });

  const getResultsDataKey = (data) =>
    JSON.stringify({
      eventType: data.eventType,
      printType: data.printType,
      wantsFlatrate: data.wantsFlatrate,
      guestCount: data.guestCount,
      delivery: data.delivery,
      postalCode: data.postalCode,
      extras: data.extras,
      digitalOptions: data.digitalOptions,
    });

  // URL-Parameter beim initialen Load lesen
  useEffect(() => {
    if (router.isReady && !isInitialized) {
      const { step, results, ...queryData } = router.query;

      // Wizard-Daten aus URL wiederherstellen (falls vorhanden)
      if (Object.keys(queryData).length > 0) {
        const restoredData = buildWizardDataFromQuery(queryData);
        setWizardData(restoredData);
      }

      // Step aus URL wiederherstellen oder Initial-URL setzen
      if (step) {
        setCurrentStep(parseInt(step, 10));
      }

      // Results-State aus URL wiederherstellen
      if (results === "true") {
        setShowResults(true);
      }

      setIsInitialized(true);
    }
  }, [router.isReady, router.query, isInitialized]);

  // Browser Back/Forward Navigation - URL-Änderungen synchronisieren
  useEffect(() => {
    if (!isInitialized) return;

    const { step, results, ...queryData } = router.query;

    // Wizard-Daten aus URL wiederherstellen
    const restoredData = buildWizardDataFromQuery(queryData);
    setWizardData(restoredData);

    // Step aktualisieren
    if (step) {
      setCurrentStep(parseInt(step, 10));
    } else {
      setCurrentStep(1);
    }

    // Results-State aktualisieren
    if (results === "true") {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [router.query, isInitialized]);

  // Virtual Pageviews beim Anzeigen eines Steps/Results tracken
  useEffect(() => {
    if (!isInitialized) return;

    const viewKey = showResults
      ? `results:${getResultsDataKey(wizardData)}`
      : `step:${currentStep}:${wizardData.printType || "na"}:${
          wizardData.delivery || "na"
        }`;

    if (lastTrackedView === viewKey) return;

    trackWizardEvent.pageViewed({
      step: currentStep,
      wizardData,
      isResults: showResults,
    });

    setLastTrackedView(viewKey);
  }, [currentStep, showResults, wizardData, isInitialized, lastTrackedView]);

  // URL aktualisieren wenn Step oder Results sich ändern
  const updateURL = (step, showResults, data = wizardData) => {
    const query = {};

    if (step > 1) {
      query.step = step.toString();
    }

    if (showResults) {
      query.results = "true";
    }

    // Wizard-Daten in URL speichern für Browser-Back
    if (data.eventType) query.eventType = data.eventType;
    if (data.printType) query.printType = data.printType;
    if (data.wantsFlatrate !== null)
      query.wantsFlatrate = data.wantsFlatrate.toString();
    if (data.guestCount) query.guestCount = data.guestCount;
    if (data.delivery) query.delivery = data.delivery;
    if (data.postalCode) query.postalCode = data.postalCode;
    if (data.extras.length > 0) query.extras = data.extras.join(",");
    if (data.digitalOptions.length > 0)
      query.digitalOptions = data.digitalOptions.join(",");

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true },
    );
  };

  // Berechne Step-Abfolge basierend auf Antworten
  const getSteps = (data = wizardData) => {
    const steps = [1]; // Step 1: Eventtyp - IMMER
    steps.push(2); // Step 2: Print-Type - IMMER

    if (data.printType === "print") {
      steps.push(3); // Step 3: Flatrate-Frage (nur bei Print)
      steps.push(4); // Step 4: Gästezahl (nur bei Print)
    }

    steps.push(5); // Step 5: Extras - IMMER
    steps.push(6); // Step 6: Digital - IMMER
    steps.push(7); // Step 7: Delivery - IMMER

    if (data.delivery === "regional") {
      steps.push(8); // Step 8: PLZ (nur wenn regional)
    }

    return steps;
  };

  const allSteps = getSteps();
  const maxStep = allSteps.length;
  const stepIndex = allSteps.indexOf(currentStep);
  const progressPercent = ((stepIndex + 1) / maxStep) * 100;

  // Lade Flatrate-Preise wenn PrintType = print
  useEffect(() => {
    if (wizardData.printType === "print" && !flatratePrices) {
      loadFlatratePrices();
    }
  }, [wizardData.printType]);

  const loadFlatratePrices = async () => {
    try {
      const res = await fetch(`/api/data?locale=${router.locale}`);
      const data = await res.json();

      let printBoxes = (data.Photobooths || []).filter((p) =>
        p.Packages?.some((pkg) => pkg.Print === true),
      );

      // Finde Flatrate vs Basis Preise
      const flatratePrices = [];
      const basicPrices = [];

      for (const box of printBoxes) {
        for (const pkg of box.Packages || []) {
          if (pkg.Print === true) {
            const totalPrice = box.PhotoboothPrice + pkg.PackagePrice;
            if (pkg.SpecialPrintFlatrate) {
              flatratePrices.push(totalPrice);
            } else {
              basicPrices.push(totalPrice);
            }
          }
        }
      }

      const minFlatrate =
        flatratePrices.length > 0 ? Math.min(...flatratePrices) : 149;
      const minBasic = basicPrices.length > 0 ? Math.min(...basicPrices) : 149;

      setFlatratePrices({
        flatratePrice: minFlatrate,
        basicPrice: minBasic,
      });
    } catch (error) {
      console.error("Fehler beim Laden der Flatrate-Preise:", error);
    }
  };

  const handleNext = async (data) => {
    const updatedData = { ...wizardData, ...data };
    setWizardData(updatedData);

    // Tracking: Erfasse die Auswahl basierend auf dem Datentyp
    if (data.eventType !== undefined) {
      trackWizardEvent.eventTypeSelected(data.eventType);
    }
    if (data.printType !== undefined) {
      trackWizardEvent.printTypeSelected(data.printType);
    }
    if (data.wantsFlatrate !== undefined) {
      trackWizardEvent.flatrateSelected(data.wantsFlatrate);
    }
    if (data.guestCount !== undefined) {
      trackWizardEvent.guestCountSelected(data.guestCount);
    }
    if (data.extras !== undefined) {
      trackWizardEvent.extrasSelected(data.extras);
    }
    if (data.digitalOptions !== undefined) {
      trackWizardEvent.digitalSelected(data.digitalOptions);
    }
    if (data.delivery !== undefined) {
      trackWizardEvent.deliverySelected(data.delivery);
    }
    if (data.postalCode !== undefined) {
      trackWizardEvent.postalCodeEntered(data.postalCode);
    }

    // Berechne Steps NEU mit den updated data (wichtig für dynamische Steps!)
    const nextSteps = getSteps(updatedData);
    const currentIndex = nextSteps.indexOf(currentStep);
    if (currentIndex < nextSteps.length - 1) {
      const nextStep = nextSteps[currentIndex + 1];
      setCurrentStep(nextStep);
      updateURL(nextStep, false, updatedData);
    } else {
      // Letzte Schritt → Results generieren
      await generateResults(updatedData);
    }
  };

  const generateResults = async (
    finalData,
    { syncUrl = true, track = true } = {},
  ) => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/data?locale=${router.locale}`);
      const data = await res.json();

      let filtered = data.Photobooths || [];

      // Filter: Print vs Digital
      if (finalData.printType === "print") {
        filtered = filtered.filter((p) =>
          p.Packages?.some((pkg) => pkg.Print === true),
        );
      } else {
        filtered = filtered.filter((p) =>
          p.Packages?.some((pkg) => pkg.Print === false),
        );
      }

      // Filter: Delivery
      if (finalData.delivery === "regional") {
        filtered = filtered.filter((p) => p.SelfDelivery || p.CustomerPickUp);
      } else {
        filtered = filtered.filter((p) => p.PostDelivery);
      }

      // Filter: Gästezahl (Print)
      if (
        finalData.printType === "print" &&
        finalData.guestCount &&
        finalData.guestCount !== "50"
      ) {
        const guestThreshold = parseInt(finalData.guestCount, 10);
        filtered = filtered.filter((p) => {
          const printPackages = (p.Packages || []).filter(
            (pkg) => pkg.Print === true,
          );
          return printPackages.some((pkg) => {
            const paperSize = pkg.Printer?.PaperStorageSize;
            const maxGuests = getMaxGuestsForPaperStorage(paperSize);
            if (maxGuests === null || maxGuests === undefined) return true;
            return maxGuests >= guestThreshold;
          });
        });
      }

      // Filter: Flatrate (Print)
      if (finalData.printType === "print" && finalData.wantsFlatrate === true) {
        filtered = filtered.filter((p) =>
          (p.Packages || []).some(
            (pkg) => pkg.Print === true && pkg.SpecialPrintFlatrate,
          ),
        );
      }

      // Filter: Extras
      if (finalData.extras.length > 0) {
        filtered = filtered.filter((p) => {
          if (finalData.extras.includes("props")) {
            if (!p.Props && !(p.Packages || []).some((pkg) => pkg.Props)) {
              return false;
            }
          }
          if (finalData.extras.includes("liveview")) {
            if (!p.ControlLiveView) return false;
          }
          return true;
        });
      }

      // Filter: Digital Options
      if (finalData.digitalOptions.length > 0) {
        filtered = filtered.filter((p) => {
          if (finalData.digitalOptions.includes("handy")) {
            if (!p.DigitalHandy) return false;
          }
          if (finalData.digitalOptions.includes("gallery")) {
            if (!p.DigitalOnlineGalerie) return false;
          }
          if (finalData.digitalOptions.includes("usb")) {
            if (!p.DigitalUSB) return false;
          }
          return true;
        });
      }

      // Sortiere: Sponsored → Preis
      filtered.sort((a, b) => {
        const aSponsored =
          a.Sponsored || (a.Packages || []).some((pkg) => pkg.Sponsored);
        const bSponsored =
          b.Sponsored || (b.Packages || []).some((pkg) => pkg.Sponsored);
        if (aSponsored !== bSponsored) {
          return bSponsored ? 1 : -1;
        }

        const priceA = Math.min(
          ...(a.Packages || [])
            .filter((pkg) => {
              if (finalData.printType === "print") return pkg.Print === true;
              else return pkg.Print === false;
            })
            .map((pkg) => a.PhotoboothPrice + pkg.PackagePrice),
          Infinity,
        );

        const priceB = Math.min(
          ...(b.Packages || [])
            .filter((pkg) => {
              if (finalData.printType === "print") return pkg.Print === true;
              else return pkg.Print === false;
            })
            .map((pkg) => b.PhotoboothPrice + pkg.PackagePrice),
          Infinity,
        );

        return priceA - priceB;
      });

      // Normalisiere
      const normalizedProducts = filtered.map((p) =>
        mapProductToCard(p, {
          printType: finalData.printType,
          guestCount: finalData.guestCount,
        }),
      );

      setProducts(normalizedProducts);
      setResultsKey(getResultsDataKey(finalData));

      // Tracking: Results angezeigt
      if (track) {
        trackWizardEvent.resultsShown(normalizedProducts, finalData);
        trackWizardEvent.wizardCompleted(finalData);
      }

      setShowResults(true);
      if (syncUrl) {
        updateURL(currentStep, true, finalData);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Produkte:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Results aus URL wiederherstellen (z.B. Browser Back von /fotobox-vergleich)
  useEffect(() => {
    if (!isInitialized || isLoading) return;

    const { results, ...queryData } = router.query;
    if (results !== "true") return;

    const restoredData = buildWizardDataFromQuery(queryData);
    const nextKey = getResultsDataKey(restoredData);

    // Nur neu berechnen, wenn Ergebnisse fehlen oder URL-Stand sich geändert hat
    if (products.length === 0 || resultsKey !== nextKey) {
      generateResults(restoredData, { syncUrl: false, track: false });
    }
  }, [router.query, isInitialized, isLoading, products.length, resultsKey]);

  const handleBack = () => {
    const currentSteps = getSteps(wizardData);
    const currentIndex = currentSteps.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = currentSteps[currentIndex - 1];
      setCurrentStep(prevStep);
      updateURL(prevStep, false);
    }
  };

  const handleRestart = () => {
    const initialData = {
      eventType: null,
      printType: null,
      wantsFlatrate: null,
      guestCount: null,
      delivery: null,
      postalCode: null,
      extras: [],
      digitalOptions: [],
    };
    setCurrentStep(1);
    setWizardData(initialData);
    setShowResults(false);
    setProducts([]);
    setResultsKey(null);
    updateURL(1, false, initialData);
  };

  if (showResults) {
    return (
      <WizardResults
        products={products}
        wizardData={wizardData}
        isLoading={isLoading}
        onRestart={handleRestart}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Die Seitenüberschrift. Die Fragen darunter sind h2 — ohne diese
          Zeile hatte die Seite bis zu den Ergebnissen keine h1, und die
          Ergebnisse bringen ihre eigene mit. */}
      <h1 className="text-center text-3xl lg:text-4xl font-bold text-slate-900">
        Finde deine perfekte Fotobox
      </h1>

      {/* Steps Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 ">
        <div className="w-full h-2 bg-slate-200 rounded-t-2xl overflow-hidden">
          <div
            className="h-full bg-[#FF5744] transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="py-8 px-6">
          {currentStep === 1 && (
            <StepEventType
              value={wizardData.eventType}
              onNext={handleNext}
              onBack={() => {}}
            />
          )}

          {currentStep === 2 && (
            <Step1PrintType
              value={wizardData.printType}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && wizardData.printType === "print" && (
            <StepFlatrate
              value={wizardData.wantsFlatrate}
              onNext={handleNext}
              onBack={handleBack}
              prices={flatratePrices}
            />
          )}

          {currentStep === 4 && wizardData.printType === "print" && (
            <Step2GuestCount
              value={wizardData.guestCount}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 5 && (
            <StepExtras
              value={wizardData.extras}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 6 && (
            <StepDigital
              value={wizardData.digitalOptions}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 7 && (
            <Step3Delivery
              value={wizardData.delivery}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 8 && wizardData.delivery === "regional" && (
            <Step4PostalCode
              value={wizardData.postalCode}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}
        </div>
      </div>
    </div>
  );
}
